import { hasPaidPropertyAccess } from '../_helpers/subscription-access';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';
import { Observable, throwError, of, BehaviorSubject, tap, catchError, retry } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { canAccessPropertyFeature, PropertyFeature } from '../_helpers/property-feature-access';

export interface OptOutPayload {
  email:               string;
  alreadyPurchased?:   boolean;
  noLongerLooking?:    boolean;
  technicalIssues?:    boolean;
  switchingService?:   boolean;
  notRelevant?:        boolean;
  manyNotifications?:  boolean;
  feedback?:           string;
}

export interface OptOutResponse {
  code:    number;
  status:  boolean;
  message: string;
}

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loggedIn$$ = new BehaviorSubject<boolean>(false);
  private userSubject = new BehaviorSubject<any>(null);

  // Refresh token queue state
  private isRefreshing = false;
  private refreshTokenSubject = new BehaviorSubject<string | null>(null);

  // Keys
  private readonly USER_KEY = 'Cirlced_User';
  private readonly ACCESS_TOKEN_KEY = 'accessToken';
  private readonly REFRESH_TOKEN_KEY = 'refreshToken';

  user$ = this.userSubject.asObservable();
  private readonly currentUser = toSignal(this.user$, { initialValue: null });

  canAccessPropertyFeature(feature: PropertyFeature): boolean {
    return canAccessPropertyFeature(this.currentUser(), feature);
  }

  canAccessPropertyListings(): boolean {
    return hasPaidPropertyAccess(this.currentUser());
  }

  constructor(
    private http: HttpClient,
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    if (this.isBrowser()) {
      this.loggedIn$$.next(this.hasUser());

      // Rehydrate user state on app start
      const raw = localStorage.getItem(this.USER_KEY);
      if (raw) this.userSubject.next(JSON.parse(raw));
    }
  }

  // ─────────────────────────────────────────────────
  // BROWSER GUARD
  // ─────────────────────────────────────────────────

  isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  private hasUser(): boolean {
    return !!localStorage.getItem(this.USER_KEY);
  }

  // ─────────────────────────────────────────────────
  // TOKEN HELPERS
  // ─────────────────────────────────────────────────

  getAccessToken(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem(this.ACCESS_TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    if (!this.isBrowser()) return null;
    return localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  getUserId(): string | null {
    const stored = this.getUser();
    const user = stored?.data ?? stored;
    return user?._id ?? user?.id ?? null;
  }

  saveTokens(accessToken: string, refreshToken: string): void {
    if (!this.isBrowser()) return;
    localStorage.setItem(this.ACCESS_TOKEN_KEY, accessToken);
    localStorage.setItem(this.REFRESH_TOKEN_KEY, refreshToken);
  }

  // ─────────────────────────────────────────────────
  // USER HELPERS
  // ─────────────────────────────────────────────────

  setUser(user: any): void {
    if (this.isBrowser()) {
      localStorage.setItem(this.USER_KEY, JSON.stringify(user));
    }
    this.userSubject.next(user);
    this.loggedIn$$.next(true);
  }

  /** Refresh profile fields without discarding credentials in older login responses. */
  updateUserProfile(profile: any): void {
    const raw = this.isBrowser() ? localStorage.getItem(this.USER_KEY) : null;
    const existing = raw ? JSON.parse(raw) : this.getUser();
    this.setUser(existing?.data
      ? { ...existing, data: { ...existing.data, ...profile } }
      : { ...existing, ...profile });
  }

  getUser(): any {
    if (this.userSubject.value) return this.userSubject.value;
    if (!this.isBrowser()) return null;
    const raw = localStorage.getItem(this.USER_KEY);
    return raw ? JSON.parse(raw) : null;
  }

  // ─────────────────────────────────────────────────
  // AUTH STATE
  // ─────────────────────────────────────────────────

  isLoggedIn(): boolean {
    if (!this.isBrowser()) return false;
    return this.hasUser();
  }

  isLoggedIn$(): Observable<boolean> {
    return this.loggedIn$$.asObservable();
  }

  // ─────────────────────────────────────────────────
  // LOGIN / LOGOUT
  // ─────────────────────────────────────────────────

  login(user: any, accessToken?: string, refreshToken?: string): void {
    if (!this.isBrowser()) return;
    this.setUser(user);
    if (accessToken && refreshToken) {
      this.saveTokens(accessToken, refreshToken);
    }
  }

  logout(): void {
    if (!this.isBrowser()) return;

    // Call backend to invalidate refresh token
    this.http.post(`${environment.api_URL}/auth/logout`, {}).subscribe({
      complete: () => this.clearSession(),
      error: () => this.clearSession(), // clear locally even if API fails
    });
  }

  clearSession(): void {
    localStorage.removeItem(this.USER_KEY);
    localStorage.removeItem(this.ACCESS_TOKEN_KEY);
    localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    localStorage.removeItem('resturnUrl');
    this.loggedIn$$.next(false);
    this.userSubject.next(null);
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  // ─────────────────────────────────────────────────
  // REFRESH TOKEN
  // ─────────────────────────────────────────────────

  get isRefreshingToken(): boolean {
    return this.isRefreshing;
  }

  get refreshSubject(): BehaviorSubject<string | null> {
    return this.refreshTokenSubject;
  }

  setRefreshing(value: boolean): void {
    this.isRefreshing = value;
  }

  refreshTokens(): Observable<any> {
    const userId = this.getUserId();
    const refreshToken = this.getRefreshToken();

    if (!userId || !refreshToken) {
      this.clearSession();
      return throwError(() => new Error('No refresh token available'));
    }

    return this.http
      .post<any>(`${environment.api_URL}/auth/refresh`, { userId, refreshToken })
      .pipe(
        tap((res) => {
          if (res?.accessToken && res?.refreshToken) {
            this.saveTokens(res.accessToken, res.refreshToken);
            this.refreshTokenSubject.next(res.accessToken);
          }
        }),
        catchError((err) => {
          this.clearSession();
          return throwError(() => err);
        })
      );
  }

  // ─────────────────────────────────────────────────
  // REDIRECT URL
  // ─────────────────────────────────────────────────

  private redirectUrl: string | null = null;

  setRedirectUrl(url: string): void {
    if (this.isBrowser()) localStorage.setItem('resturnUrl', url);
    this.redirectUrl = url;
  }

  getRedirectUrl(): string | null {
    return this.redirectUrl ?? (this.isBrowser() ? localStorage.getItem('resturnUrl') : null);
  }

  clearRedirectUrl(): void {
    if (this.isBrowser()) localStorage.removeItem('resturnUrl');
    this.redirectUrl = null;
  }

  // ─────────────────────────────────────────────────
  // API CALLS
  // ─────────────────────────────────────────────────

  userLogin(data: any): Observable<any> {
    return this.http.post(`${environment.api_URL}/auth/login`, data).pipe(
      tap((res: any) => {
        // Admin login returns tokens directly
        if (res?.data?.accessToken) {
          this.login(res.data, res.data.accessToken, res.data.refreshToken);
        }
      }),
      catchError(this.handleError.bind(this))
    );
  }
  getUserDetails(): Observable<any> {
    return this.http.get(`${environment.api_URL}/users/profile`).pipe(
      tap((res: any) => {
        // Admin login returns tokens directly
        if (res?.data?.accessToken) {
          this.login(res.data, res.data.accessToken, res.data.refreshToken);
        }
      }),
      catchError(this.handleError.bind(this))
    );
  }

  otpVerification(data: any): Observable<any> {
    return this.http.post(`${environment.api_URL}/auth/verifyOtp`, data).pipe(
      tap((res: any) => {
        // Non-admin tokens arrive after OTP verification
        if (res?.accessToken) {
          this.saveTokens(res.accessToken, res.refreshToken);
          if (res?.data) this.setUser(res.data);
        }
      }),
      catchError(this.handleError.bind(this))
    );
  }
  getCountryCode(): Observable<any> {
    return this.http.get(`${environment.api_URL}/dashboard/contCode`).pipe(
      tap((res: any) => {
      }),
      catchError(this.handleError.bind(this))
    );
  }

  getUserProfile(): Observable<any> {
    return this.http
      .get(`${environment.api_URL}/users/profile`)
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }

  userSignUp(data: any): Observable<any> {
    return this.http
      .post(`${environment.api_URL}/auth/signup`, data)
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }

  reSendOtp(data: any): Observable<any> {
    return this.http
      .post(`${environment.api_URL}/auth/resendOtp`, data)
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }

  forgotPWS(data: any): Observable<any> {
    return this.http
      .post(`${environment.api_URL}/auth/forgot-password`, data)
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }

  verifyOtpPws(data: any): Observable<any> {
    return this.http
      .post(`${environment.api_URL}/auth/verifyOtpPws`, data)
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }
  optOut(payload: OptOutPayload): Observable<OptOutResponse> {
    console.log('payload',payload)
    return this.http
    .post<OptOutResponse>(`${environment.api_URL}/users/opt-out`, payload)
    .pipe(retry(1), catchError(this.handleError.bind(this)));
  }

  updatePWS(data: any): Observable<any> {
    return this.http
      .patch(`${environment.api_URL}/auth/update-password`, data)
      .pipe(retry(1), catchError(this.handleError.bind(this)));
  }

  

  private handleError(error: HttpErrorResponse): Observable<never> {
    if (!this.isBrowser()) return of({ status: false, message: 'SSR mode' }) as any;
    return throwError(() => ({
      status: false,
      message: this.getErrorMessage(error),
      error,
    }));
  }

  private getErrorMessage(error: HttpErrorResponse): string {
    if (error?.error?.message) return error.error.message;
    switch (error.status) {
      case 0:   return 'Network error. Please check your connection.';
      case 400: return 'Bad request.';
      case 401: return 'Unauthorized.';
      case 403: return 'Access denied.';
      case 404: return 'Not found.';
      case 500: return 'Server error.';
      default:  return 'Something went wrong.';
    }
  }
}
